import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Hash,
  Info,
  CheckCircle,
  XCircle,
} from "lucide-react";

const TwoSumVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Basic case",
      array: [2, 7, 11, 15],
      target: 9,
    },
    {
      id: 2,
      name: "Middle elements",
      array: [3, 2, 4],
      target: 6,
    },
    {
      id: 3,
      name: "Negative numbers",
      array: [-1, -2, -3, -4, -5],
      target: -8,
    },
    {
      id: 4,
      name: "Duplicate values",
      array: [3, 3],
      target: 6,
    },
    {
      id: 5,
      name: "Large array",
      array: [1, 5, 3, 7, 8, 9, 2, 4],
      target: 11,
    },
    {
      id: 6,
      name: "Zero sum",
      array: [-3, 4, 3, 90],
      target: 0,
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [customTarget, setCustomTarget] = useState("");
  const [array, setArray] = useState(defaultTestCases[0].array);
  const [target, setTarget] = useState(defaultTestCases[0].target);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);
  const [approach, setApproach] = useState("hashmap"); // "hashmap" or "bruteforce"

  // Pseudo code
  const hashMapPseudoCode = [
    { line: "function twoSum(nums, target):", indent: 0 },
    { line: "hashMap = {}", indent: 1 },
    { line: "for i = 0 to nums.length - 1:", indent: 1 },
    { line: "complement = target - nums[i]", indent: 2 },
    { line: "if complement in hashMap:", indent: 2 },
    { line: "return [hashMap[complement], i]", indent: 3 },
    { line: "hashMap[nums[i]] = i", indent: 2 },
    { line: "return []", indent: 1 },
  ];

  const bruteForcePseudoCode = [
    { line: "function twoSum(nums, target):", indent: 0 },
    { line: "for i = 0 to nums.length - 1:", indent: 1 },
    { line: "for j = i + 1 to nums.length - 1:", indent: 2 },
    { line: "if nums[i] + nums[j] == target:", indent: 3 },
    { line: "return [i, j]", indent: 4 },
    { line: "return []", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  const calculateHashMapSteps = (nums, target) => {
    const allSteps = [];
    let stepCount = 0;
    let hashMap = {};
    let result = null;

    const addStep = (desc, hl, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description: desc,
        highlightLine: hl,
        hashMap: { ...hashMap },
        result,
        ...details,
      });
    };

    addStep("Start: Initialize an empty hash map.", 1);

    for (let i = 0; i < nums.length; i++) {
      const complement = target - nums[i];
      addStep(
        `At index ${i}, value is ${nums[i]}. Calculate complement: ${target} - ${nums[i]} = ${complement}.`,
        3,
        { currentIndex: i, complement }
      );

      if (hashMap[complement] !== undefined) {
        result = [hashMap[complement], i];
        addStep(
          `Complement ${complement} found in map. Pair is [${result[0]}, ${result[1]}].`,
          5,
          { currentIndex: i, complement, foundPair: result, isFinal: true }
        );
        return allSteps;
      }

      addStep(
        `Complement ${complement} not in map. Add current value ${nums[i]} and index ${i} to map.`,
        6,
        { currentIndex: i, complement }
      );
      hashMap[nums[i]] = i;
    }

    addStep("No solution found after checking all elements.", 7, {
      isFinal: true,
      result: [],
    });
    return allSteps;
  };

  const calculateBruteForceSteps = (nums, target) => {
    const allSteps = [];
    let stepCount = 0;
    let result = null;

    const addStep = (desc, hl, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description: desc,
        highlightLine: hl,
        result,
        ...details,
      });
    };

    addStep("Start: Begin nested loops.", 1);

    for (let i = 0; i < nums.length; i++) {
      addStep(`Outer loop: i = ${i}.`, 1, { outerIndex: i });
      for (let j = i + 1; j < nums.length; j++) {
        addStep(
          `Inner loop: j = ${j}. Checking pair (${nums[i]}, ${nums[j]}).`,
          2,
          { outerIndex: i, innerIndex: j }
        );
        if (nums[i] + nums[j] === target) {
          result = [i, j];
          addStep(`Sum is ${target}. Pair found: [${i}, ${j}].`, 4, {
            outerIndex: i,
            innerIndex: j,
            foundPair: result,
            isFinal: true,
          });
          return allSteps;
        }
      }
    }

    addStep("No solution found after checking all pairs.", 5, {
      isFinal: true,
      result: [],
    });
    return allSteps;
  };

  useEffect(() => {
    const newSteps =
      approach === "hashmap"
        ? calculateHashMapSteps(array, target)
        : calculateBruteForceSteps(array, target);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [array, target, approach]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setArray(testCases[index].array);
    setTarget(testCases[index].target);
  };

  const addCustomTestCase = () => {
    try {
      const nums = JSON.parse(`[${customInput}]`);
      const targetNum = parseInt(customTarget);
      if (nums.some(isNaN) || isNaN(targetNum))
        throw new Error("Invalid number format.");
      const newTestCase = {
        id: Date.now(),
        name: `Custom`,
        array: nums,
        target: targetNum,
      };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
      setCustomTarget("");
    } catch (error) {
      alert("Invalid format. Use comma-separated numbers for array.");
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || {};

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Hash className="text-blue-500" /> Two Sum
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given an array of integers `nums` and an integer `target`,
                return indices of the two numbers such that they add up to
                `target`.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      nums: [{tc.array.join(", ")}], target: {tc.target}
                    </div>
                  </button>
                  {tc.id > 6 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Array: 2,7,11,15"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <input
                type="number"
                value={customTarget}
                onChange={(e) => setCustomTarget(e.target.value)}
                placeholder="Target: 9"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setApproach("hashmap");
                }}
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  approach === "hashmap"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                Hash Map
              </button>
              <button
                onClick={() => {
                  setApproach("bruteforce");
                }}
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  approach === "bruteforce"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                Brute Force
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div className="my-4">
              <div className="mb-4 p-2 bg-yellow-50 rounded text-center">
                <span className="text-sm font-medium">Target: </span>
                <span className="text-lg font-bold text-yellow-700">
                  {target}
                </span>
                {approach === "hashmap" &&
                  currentStepData.complement !== undefined && (
                    <span className="ml-4 text-sm">
                      Looking for:{" "}
                      <span className="font-bold">
                        {currentStepData.complement}
                      </span>
                    </span>
                  )}
              </div>

              <div className="flex flex-wrap gap-2">
                {array.map((num, idx) => {
                  const isFound = currentStepData.foundPair?.includes(idx);
                  const isOuter =
                    approach === "bruteforce" &&
                    currentStepData.outerIndex === idx;
                  const isInner =
                    approach === "bruteforce" &&
                    currentStepData.innerIndex === idx;
                  const isCurrent =
                    approach === "hashmap" &&
                    currentStepData.currentIndex === idx;

                  return (
                    <div key={idx} className="text-center">
                      <div
                        className={`w-14 h-14 flex items-center justify-center border-2 rounded font-mono text-lg font-bold transition-all duration-300
                        ${
                          isFound
                            ? "bg-green-200 border-green-400 scale-110"
                            : isOuter
                            ? "bg-purple-200 border-purple-400"
                            : isInner
                            ? "bg-orange-200 border-orange-400"
                            : isCurrent
                            ? "bg-blue-200 border-blue-400"
                            : "bg-gray-100 border-gray-300"
                        }`}
                      >
                        {num}
                      </div>
                      <span className="text-xs text-gray-500">{idx}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {approach === "hashmap" && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-gray-700">
                  Hash Map
                </h4>
                <div className="p-2 bg-gray-50 rounded mt-1 flex flex-wrap gap-2 justify-center min-h-[40px]">
                  {currentStepData &&
                    currentStepData.hashMap &&
                    Object.entries(currentStepData.hashMap).map(
                      ([key, val]) => (
                        <span
                          key={key}
                          className="font-mono text-xs p-1 bg-gray-200 rounded"
                        >
                          {key} → {val}
                        </span>
                      )
                    )}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5">
              {(approach === "hashmap"
                ? hashMapPseudoCode
                : bruteForcePseudoCode
              ).map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <div className="space-y-3">
              <div
                className={
                  approach === "hashmap" ? "p-2 bg-blue-50 rounded" : ""
                }
              >
                <h4 className="text-xs font-bold">Hash Map (O(n))</h4>
                <p className="text-xs text-gray-600">
                  Iterate through the array once. For each element, calculate
                  its complement. If the complement exists in the hash map, we
                  have a solution. Otherwise, store the current element and its
                  index in the map.
                </p>
              </div>
              <div
                className={
                  approach === "bruteforce" ? "p-2 bg-blue-50 rounded" : ""
                }
              >
                <h4 className="text-xs font-bold">Brute Force (O(n²))</h4>
                <p className="text-xs text-gray-600">
                  Use two nested loops to check every possible pair of numbers
                  in the array to see if their sum equals the target.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The Hash Map approach trades space (O(n)) for a significant
                  speed improvement (O(n) vs O(n²)).
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Looking for `target - current_number` is the key insight that
                  makes the hash map solution possible.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Brute force is simple to conceptualize but becomes very slow
                  on large inputs.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoSumVisualizer;
